import React, { useLayoutEffect, useRef, useState } from 'react'
import { Box, Textarea, Text, Stack, UserChip } from '@/components/lib'
import {
  findActiveMention,
  applyMention,
  applyDisplayEdit,
  toPlainText,
  mentionAt,
  displayName
} from '@/utils/mentions'

const MAX_SUGGESTIONS = 6

// Keys that move the caret without changing the text, and so can carry it into
// or out of an unfinished mention.
const CARET_KEYS = ['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Home', 'End']

/**
 * A textarea that can @-mention people.
 *
 * Typing `@` opens a list of the people passed in `users`; choosing one inserts
 * their name. `users` is the caller's business: pass the people who can see the
 * thing being written on, because the API drops a mention of anyone who can't
 * and the mention would then silently do nothing.
 *
 * `value` and `onChange` are in the stored form, with a `@[Name](user:id)`
 * token per mention (see `utils/mentions.js`), so this is a drop-in for the
 * Textarea it replaces. What is *shown* and typed is the display form, where a
 * mention is just `@Name` — nobody should have to read markup while writing a
 * sentence. Every edit is translated back onto the value underneath, and the
 * caret put back where the writer left it, since replacing a controlled
 * textarea's value would otherwise drop it at the end.
 */
export default function MentionInput ({ users, value, onChange, onSubmit, ...textareaProps }) {
  const inputRef = useRef(null)
  const pendingCaret = useRef(null)
  const [active, setActive] = useState(null)
  const [highlighted, setHighlighted] = useState(0)

  const display = toPlainText(value)

  const suggestions = active
    ? (users || []).filter(u => matches(u, active.query)).slice(0, MAX_SUGGESTIONS)
    : []
  const isOpen = suggestions.length > 0

  // Runs after the new value has been painted, so the caret lands in the text
  // the writer is actually looking at.
  useLayoutEffect(() => {
    const caret = pendingCaret.current
    if (caret == null || !inputRef.current) return
    pendingCaret.current = null
    inputRef.current.setSelectionRange(caret, caret)
  })

  // Takes the markup and a display caret. The markup is passed in rather than
  // read from `value`, because after an edit the new one has not reached this
  // component as a prop yet.
  function refresh (markup, caret) {
    // Sitting in the middle of a finished name is not someone reaching for the
    // picker — they are reading, or about to delete it.
    const found = mentionAt(markup, caret)
      ? null
      : findActiveMention(toPlainText(markup), caret)
    // Idempotent, so this can be called on any caret movement: re-reporting the
    // same unfinished mention must not reset which suggestion is highlighted,
    // or the arrow keys could never move down the list.
    if (found && active && found.start === active.start && found.query === active.query) return
    setActive(found)
    setHighlighted(0)
  }

  function handleChange (e) {
    const result = applyDisplayEdit(value, e.target.value, e.target.selectionStart)
    pendingCaret.current = result.caret
    onChange(result.text)
    refresh(result.text, result.caret)
  }

  function choose (user) {
    const caret = inputRef.current ? inputRef.current.selectionStart : display.length
    const result = applyMention(value, active.start, caret, user)
    pendingCaret.current = result.caret
    onChange(result.text)
    setActive(null)
    if (inputRef.current) inputRef.current.focus()
  }

  function handleKeyDown (e) {
    if (isOpen) {
      if (e.key === 'ArrowDown') {
        e.preventDefault()
        setHighlighted(h => (h + 1) % suggestions.length)
        return
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault()
        setHighlighted(h => (h - 1 + suggestions.length) % suggestions.length)
        return
      }
      if (e.key === 'Enter' || e.key === 'Tab') {
        e.preventDefault()
        choose(suggestions[highlighted])
        return
      }
      if (e.key === 'Escape') {
        e.preventDefault()
        setActive(null)
        return
      }
    }
    // Ctrl/Cmd+Enter posts, so a comment doesn't need a trip to the mouse. A
    // plain Enter stays a new line — comments run to several of them.
    if (onSubmit && e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
      e.preventDefault()
      onSubmit()
    }
  }

  return (
    <Box position='relative'>
      <Textarea
        ref={inputRef}
        value={display}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        // Moving the caret without changing the text can also land it in or
        // out of an unfinished mention.
        onClick={e => refresh(value, e.target.selectionStart)}
        onKeyUp={e => {
          if (CARET_KEYS.includes(e.key)) refresh(value, e.target.selectionStart)
        }}
        onBlur={() => window.setTimeout(() => setActive(null), 150)}
        {...textareaProps}
      />
      {isOpen &&
        <Stack
          position='absolute'
          zIndex='dropdown'
          left={0}
          right={0}
          top='100%'
          mt={1}
          p={1}
          gap={0}
          maxH='240px'
          overflowY='auto'
          bg='bg.surface'
          borderWidth='1px'
          borderColor='border.default'
          rounded='md'
          shadow='lg'
        >
          {suggestions.map((user, i) => (
            <Box
              key={user._id}
              px={2}
              py={1}
              rounded='sm'
              cursor='pointer'
              bg={i === highlighted ? 'primary.subtle' : undefined}
              onMouseEnter={() => setHighlighted(i)}
              // The textarea's blur would close the list before a click landed.
              onMouseDown={e => e.preventDefault()}
              onClick={() => choose(user)}
            >
              <UserChip compact user={user} />
            </Box>
          ))}
          <Text px={2} pt={1} fontSize='xs' color='fg.muted'>
            Mentioned people are notified.
          </Text>
        </Stack>}
    </Box>
  )
}

function matches (user, query) {
  if (!query) return true
  const needle = query.toLowerCase()
  return `${displayName(user)} ${user.email || ''}`.toLowerCase().includes(needle)
}
